import type { ComponentProps } from 'react';
import React from 'react';
import styled from '@emotion/styled';
import { ArticleLabelType } from 'graphql-ts-types';
import type { BorderRadiusProps, DisplayProps, HeightProps, PositionProps } from 'styled-system';
import { borderRadius, display, position } from 'styled-system';

import { sortLabels } from 'common/components/ProductCard/utils/sort-labels';
import { GrowthbookFlag } from 'common/constants/feature-flags';
import { useFeatureFlagIsOn } from 'common/hooks/feature-flags';
import { focusOutlineCSS } from 'common/theme/styles';
import { getMinPrice } from '../../../product-list-view/utils';
import type {
  CursorProps,
  TextDecorationProps,
  ThemeHeightProps,
  ThemeRightProps,
  ThemeTopProps,
} from '../../theme/system-utilities';
import {
  cursor,
  textDecoration,
  themeHeight,
  themeRight,
  themeTop,
} from '../../theme/system-utilities';
import type { ProductCardProps } from '../../types/product-card';
import { Flex } from '../Flex';
import WishlistButtonV2 from '../WishlistButton/WishlistButtonV2';
import ProductCardImage from './components/ProductCardImage';
import { ProductCardImageLabels } from './components/ProductCardImageLabels';
import { ProductDetailsSection } from './components/ProductDetailsSection';
import { DEFAULT_PRODUCTS_ABOVE_THE_FOLD } from './constants/constants';
import { getLinkTrackingProps } from './tracking/tracking';

const ICON_SIZE = 17;

const Container = styled.div<PositionProps>`
  ${position};
`;

const LinkContainer = styled.a<
  CursorProps &
    TextDecorationProps &
    PositionProps &
    DisplayProps &
    ThemeHeightProps &
    BorderRadiusProps
>`
  ${cursor};
  ${textDecoration};
  ${position};
  ${display};
  ${themeHeight};
  ${borderRadius};
  ${focusOutlineCSS};
`;

const linkContainerStyles: ComponentProps<typeof LinkContainer> = {
  cursor: 'pointer',
  textDecoration: 'none',
  position: 'relative',
  display: 'block',
  themeHeight: '100%',
  borderRadius: 'medium',
};

const InnerContainer = styled(Flex)`
  ${cursor};
  ${textDecoration};
`;

const innerContainerStyles: ComponentProps<typeof InnerContainer> = {
  flexDirection: 'column',
  p: 'sp_8',
};
const innerContainerStylesV2: ComponentProps<typeof InnerContainer> = {
  flexDirection: 'column',
  p: ['sp_8', null, null, 'sp_12'],
};

const StyledWishlistButton = styled(WishlistButtonV2)<
  PositionProps & ThemeRightProps & ThemeTopProps & HeightProps
>`
  ${position};
  ${themeRight};
  ${themeTop};
`;

const wishlistButtonStyles: Partial<ComponentProps<typeof StyledWishlistButton>> = {
  position: 'absolute',
  themeRight: 'sp_12',
  themeTop: 'sp_12',
  height: '32px',
};

const ProductCard = ({
  article,
  index,
  productEventDataListType: list,
  visibleProductsCount = DEFAULT_PRODUCTS_ABOVE_THE_FOLD,
  isMarketplace,
  ref,
  withProminentPricing,
  isSmallVariant = false,
}: ProductCardProps) => {
  const {
    sku,
    url,
    name,
    images,
    labels_v2,
    tracking,
    sponsored,
    isUpdated,
    metrics,
    brand,
    campaigns,
  } = article;
  const withPDPAutoScroll = useFeatureFlagIsOn(GrowthbookFlag.PDPAutoScroll);
  const isV2 = useFeatureFlagIsOn(GrowthbookFlag.ProductCardV2);

  const home24Selection = labels_v2.find(label => label?.type === ArticleLabelType.Home24Selection);
  const minPrice = getMinPrice({
    prices: article.prices,
    variationsMinPrice: article.variationsMinPrice,
  });
  const sortedLabels = sortLabels(labels_v2, home24Selection);
  const campaignsSafe = campaigns ?? [];
  const resolvedInnerContainerStyles = isV2 ? innerContainerStylesV2 : innerContainerStyles;

  return (
    <LinkContainer
      {...linkContainerStyles}
      rel="noopener noreferrer"
      data-testid="product-card-inner-container"
      {...getLinkTrackingProps({
        href: `${url}${withPDPAutoScroll ? '#main-content' : ''}`,
        tracking,
        list,
        index,
        sponsored,
        hasMinPrice: !!minPrice,
      })}
      ref={ref}
      target="_blank"
    >
      <InnerContainer {...resolvedInnerContainerStyles}>
        <Container position="relative">
          <ProductCardImage
            images={images}
            index={index}
            isUpdated={Boolean(isUpdated)}
            visibleProductsCount={visibleProductsCount}
            name={name}
          />
          <ProductCardImageLabels
            labels_v2={labels_v2}
            sponsored={sponsored}
            brand={brand?.name ?? ''}
            campaigns={campaignsSafe}
            isSmallVariant={isSmallVariant}
          />
        </Container>
        <ProductDetailsSection
          isSmallVariant={isSmallVariant}
          article={article}
          productIndex={index}
          visibleProductsCount={visibleProductsCount}
          isMarketplace={isMarketplace}
          sortedLabels={sortedLabels}
          minPrice={minPrice}
          withProminentPricing={withProminentPricing}
          isV2={isV2}
        />
      </InnerContainer>
      <StyledWishlistButton
        {...wishlistButtonStyles}
        sku={sku}
        count={metrics?.wishlist.count}
        index={index}
        tracking={tracking}
        data-cnstrc-btn="add_to_wishlist"
        className="product-card-wishlist"
        size={ICON_SIZE}
      />
    </LinkContainer>
  );
};

ProductCard.displayName = 'ProductCard';

export default ProductCard;
